import { useState } from 'react';

interface Params<T> {
	value: T;
	isLatched: boolean;
}

/**
 * useLatchedValue is a React hook for "latching" a value while a given condition (isLatched) is true.
 *
 * - When isLatched is true, the hook retains ("latches") the previous value even if the incoming value changes.
 * - When isLatched becomes false, the hook updates to the latest value and will continue updating if isLatched remains false.
 *
 * Useful for scenarios where you want to prevent the sudden update of a value during loading or placeholder states,
 * and only update once fresh/confirmed data is available.
 *
 * @param value      The value you want to latch.
 * @param isLatched  If true, the hook holds onto the current value until this becomes false.
 * @returns          The current latched or incoming value, depending on isLatched.
 */
export default function useLatchedValue<T>({ value, isLatched }: Params<T>) {
	const [activeValue, setActiveValue] = useState(value);

	if (!isLatched && activeValue !== value) {
		setActiveValue(value);
	}

	return activeValue;
}
